<template>
  <div
    class="size-full overflow-y-auto overflow-x-hidden sm:px-6 lg:px-8"
  >
    <div
      class="flex min-h-full flex-col justify-center py-12"
    >
      <div class="mx-auto max-w-sm">
        <BaseCard class="relative">
          <BaseInnerLoading
            v-if="isValidatingToken || isSubmitting"
          />

          <div v-if="tokenValid" class="space-y-4">
            <h2 class="text-xl font-semibold">
              Reset Password
            </h2>
            <p v-if="userEmail" class="text-gray-600">
              Setting new password for
              <strong>{{ userEmail }}</strong>
            </p>
            <SetChangeResetPasswordForm
              :require-current-password="false"
              @submit="onSubmit"
            />
          </div>

          <div v-else class="space-y-4 text-center">
            <IconExclamationTriangle
              class="mx-auto size-12 text-red-500"
            />
            <p class="text-red-600">{{
              tokenInvalidErrorMessage
            }}</p>
            <ButtonPrimary
              @click="router.push({ name: 'login' })"
            >
              Back to Login
            </ButtonPrimary>
          </div>
        </BaseCard>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  SetChangeResetPasswordFormInput,
  SetChangeResetPasswordFormOutput,
} from "@/composables/forms/useSetChangeResetPasswordForm"
import type { SubmissionContext } from "vee-validate"

const route = useRoute()
const router = useRouter()

const passwordResetToken = route.query.token as string // Typeguard below isn't recognised by vue-tsc

if (
  !passwordResetToken ||
  !(typeof passwordResetToken === "string")
) {
  router.replace({ name: "login" })
}

const tokenValid = ref(true)
const isSubmitting = ref(false)
const isValidatingToken = ref(false)
const userEmail = ref<string | null>(null)
const tokenInvalidErrorMessage = ref<string | null>(null)

const TOKEN_VALIDATION_ERROR_MESSAGES: Record<
  string,
  string
> = {
  "password-already-reset":
    "Password has already been reset since this link was issued. Please request a new link.",
  "token-expired":
    "This password reset link has expired. Please request a new one.",
  "invalid-token": "Invalid password reset link",
}

onMounted(async () => {
  isValidatingToken.value = true
  try {
    const response = await validatePasswordResetToken(
      passwordResetToken
    )
    userEmail.value = response.email
    tokenValid.value = true
  } catch (error) {
    tokenValid.value = false

    if (error instanceof ApiError && error.code) {
      tokenInvalidErrorMessage.value =
        TOKEN_VALIDATION_ERROR_MESSAGES[error.code] ||
        "Password reset token validation failed"
    } else {
      tokenInvalidErrorMessage.value =
        "Password reset token validation failed"
    }
  } finally {
    isValidatingToken.value = false
  }
})

const onSubmit = async (
  params: SetChangeResetPasswordFormOutput,
  context: SubmissionContext<SetChangeResetPasswordFormInput>
) => {
  isSubmitting.value = true
  try {
    await resetPassword({
      token: passwordResetToken,
      password: params.password,
    })
    toast.success("Password reset successfully")
    router.push({ name: "login" })
  } catch (error) {
    if (!(error instanceof ApiError)) {
      toast.error("Failed to reset password")
      return
    }

    switch (error.code) {
      case "token-expired":
      case "invalid-token":
      case "password-already-reset":
        tokenValid.value = false
        tokenInvalidErrorMessage.value =
          TOKEN_VALIDATION_ERROR_MESSAGES[error.code]
        break
      case "same-as-current-password":
      case "invalid-new-password":
        context.setFieldError(
          "password",
          error.detail ||
            "Password does not meet requirements"
        )
        break
      default:
        toast.error("Failed to reset password")
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>
